import React, {useEffect, useState} from 'react';
import {connect} from 'react-redux';
import {
  ISupplierCompanyProfile,
  ISupplierUserProfile,
} from 'constants/interfaces';
import {AppBar, Snackbar, Tab, Tabs} from '@material-ui/core';
import Team from 'screens/Dashboard/Team/Team';
import {Depots} from 'screens/Dashboard/Depots/Depots';
import {apiGetCompany} from 'constants/Api';
import NavigationService from 'services/NavigationService';
import {Route, Switch, useRouteMatch} from 'react-router-dom';
import {Skeleton} from '@material-ui/lab';
import {AddOrUpdateCompany} from 'screens/Dashboard/Company/AddOrUpdateCompany';
import {RouteComponentProps, useLocation} from 'react-router';
import {DepotState} from 'appredux/reducers/reducer-depots';
import {PermissionsService} from 'services/PermissionsService';
import Wallet from '../Wallet/Wallet';

type CompanyProps = {
  user: ISupplierUserProfile;
  depotState: DepotState;
};

/**
 * Component used to display team members
 */
class Company extends React.Component<CompanyProps> {
  state: {
    tabIndex: number;
    isLoading: boolean;
    company?: ISupplierCompanyProfile;
    showUpdateCompanySuccessSnackbar: boolean;
  } = {
    tabIndex: 0,
    isLoading: false,
    company: undefined,
    showUpdateCompanySuccessSnackbar: false,
  };

  componentDidMount() {
    this.refreshCompany();
  }

  refreshCompany() {
    this.setState({isLoading: true}, () => {
      const user = this.props.user;
      apiGetCompany(user.company?.id.toString() || '')
        .then((company: ISupplierCompanyProfile) => {
          this.setState({company, isLoading: false});
        })
        .catch((err) => {
          alert('Error loading company. Please try again later');
          console.error(err);
          this.setState({company: undefined});
        })
        .finally(() => this.setState({isLoading: false}));
    });
  }

  render() {
    const user = this.props.user;
    const {isLoading, company, showUpdateCompanySuccessSnackbar} = this.state;
    return (
      <div className="company-container">
        <CompanyTabs
          user={user}
          overview={() => (
            <div className="table-screen company-overview-screen">
              {isLoading && (
                <Skeleton
                  className="company-overview-skeleton"
                  variant="rect"
                  height={670}
                  width="100%"
                  style={{borderRadius: 8}}
                />
              )}
              {!isLoading && (
                <AddOrUpdateCompany
                  companyTab
                  user={user}
                  company={company}
                  onUpdate={() => {
                    this.setState({showUpdateCompanySuccessSnackbar: true});
                    this.refreshCompany();
                    setTimeout(() => window.location.reload(), 2000);
                  }}
                />
              )}
            </div>
          )}
          depots={() => (
            <Depots user={user} depotState={this.props.depotState} hideTitle />
          )}
          team={() => <Team hideTitle />}
          wallet={() => <Wallet hideTitle />}
        />
        <Snackbar
          anchorOrigin={{vertical: 'bottom', horizontal: 'center'}}
          open={showUpdateCompanySuccessSnackbar}
          onClose={() =>
            this.setState({
              showUpdateCompanySuccessSnackbar: false,
            })
          }
          autoHideDuration={3000}
          message="Company updated successfully"
        />
      </div>
    );
  }
}

type ReactRouterDomComponent =
  | React.ComponentType<RouteComponentProps<any>>
  | React.ComponentType<any>;

function CompanyTabs(props: {
  user: ISupplierUserProfile;
  overview: ReactRouterDomComponent;
  depots: ReactRouterDomComponent;
  team: ReactRouterDomComponent;
  wallet: ReactRouterDomComponent;
}) {
  const {path, url} = useRouteMatch();
  const location = useLocation();
  const [tabIndex, setTabIndex] = useState(0);

  const permissions = new PermissionsService(props.user);

  useEffect(() => {
    const currentTabIndex = location.pathname.includes('/wallet')
      ? 3
      : location.pathname.includes('/team')
      ? 2
      : location.pathname.includes('/depots')
      ? 1
      : 0;
    setTabIndex(currentTabIndex);
  }, [location.pathname]);

  return (
    <>
      <AppBar
        position="relative"
        elevation={0}
        variant="outlined"
        className="company-tabs"
      >
        <Tabs
          value={tabIndex}
          onChange={(event: React.ChangeEvent<{}>, tabIndex: number) => {
            switch (tabIndex) {
              default:
              case 0:
                return NavigationService.navigate(`${url}`);
              case 1:
                return NavigationService.navigate(`${url}/depots`);
              case 2:
                return NavigationService.navigate(`${url}/team`);
              case 3:
                return NavigationService.navigate(`${url}/wallet`);
            }
          }}
          indicatorColor="primary"
          textColor="primary"
        >
          {permissions.shouldShowCompany() && (
            <Tab value={0} label="Overview" />
          )}
          {permissions.shouldShowDepots() && <Tab value={1} label="Depots" />}
          {permissions.shouldShowTeam() && <Tab value={2} label="Team" />}
          {permissions.shouldShowWallet() && <Tab value={3} label="Wallet" />}
        </Tabs>
      </AppBar>
      <Switch>
        {permissions.shouldShowCompany() && (
          <Route path={path} exact component={props.overview} />
        )}
        {permissions.shouldShowDepots() && (
          <Route path={`${path}/depots`} component={props.depots} />
        )}
        {permissions.shouldShowTeam() && (
          <Route path={`${path}/team`} component={props.team} />
        )}
        {permissions.shouldShowWallet() && (
          <Route path={`${path}/wallet`} component={props.wallet} />
        )}
      </Switch>
    </>
  );
}

const mapStateToProps = (state: any) => {
  return {
    user: state.user,
    depotState: state.depots,
  };
};

export default connect(mapStateToProps)(Company);
